/**
 * Sampled hit sounds — shared by every editor.
 *
 * Until now every editor sound was synthesised from oscillators. Users asked
 * for the note hit from the Unity (desktop) editor, which is a recorded clap,
 * so this module adds the one thing the synth path can't do: play a real
 * sample, sample-accurately, on whichever AudioContext the caller owns.
 *
 * Design notes (the app has SEVERAL AudioContexts — Flow's shared SFX/music
 * context, the SR tick context, Console's, …):
 *   • the file bytes are fetched ONCE (no user gesture needed) and kept as an
 *     ArrayBuffer;
 *   • the decoded AudioBuffer is cached PER CONTEXT (a buffer belongs to the
 *     context that decoded it, and contexts can differ in sample rate);
 *   • decodeAudioData DETACHES the buffer it is given, so every decode gets a
 *     private `slice(0)` copy — same rule as flowAudioEngine/consoleAudio;
 *   • playback is fire-and-forget: a fresh BufferSource per hit (they're
 *     one-shot), disconnected on ended so dense charts don't leak nodes;
 *   • `playHitSample` is SYNCHRONOUS and returns false when the sample isn't
 *     decoded yet, so callers can fall back to their oscillator voice instead
 *     of dropping the hit or awaiting inside the audio path.
 */

/** Sampled voices available in the hit-sound roster. */
export type HitSampleName = 'clap';

const SAMPLE_URLS: Record<HitSampleName, string> = {
  // The Synths Riders Unity editor's HitSound — a short, clacky clap.
  clap: '/audio/clap.wav',
};

/** Raw file bytes, fetched once per sample. */
const rawBytes = new Map<HitSampleName, Promise<ArrayBuffer | null>>();
/** Decoded buffers, per AudioContext (a buffer can't cross contexts). */
const decoded = new WeakMap<BaseAudioContext, Map<HitSampleName, AudioBuffer>>();
/** In-flight decodes, so N callers in the same frame share one decode. */
const decoding = new WeakMap<BaseAudioContext, Set<HitSampleName>>();

function fetchBytes(name: HitSampleName): Promise<ArrayBuffer | null> {
  let p = rawBytes.get(name);
  if (!p) {
    p = fetch(SAMPLE_URLS[name])
      .then(r => (r.ok ? r.arrayBuffer() : null))
      .catch(() => null);
    rawBytes.set(name, p);
  }
  return p;
}

/** Kick the network fetch early (safe before any user gesture). */
export function preloadHitSamples(): void {
  (Object.keys(SAMPLE_URLS) as HitSampleName[]).forEach(fetchBytes);
}

/**
 * Ensure `name` is decoded for `ctx`. Safe to call repeatedly (and from a
 * first-gesture warm-up); resolves once the buffer is ready.
 */
export async function ensureHitSample(ctx: BaseAudioContext, name: HitSampleName): Promise<AudioBuffer | null> {
  const perCtx = decoded.get(ctx);
  const hit = perCtx?.get(name);
  if (hit) return hit;

  const busy = decoding.get(ctx) ?? new Set<HitSampleName>();
  decoding.set(ctx, busy);
  if (busy.has(name)) return null;      // someone else is decoding it
  busy.add(name);
  try {
    const bytes = await fetchBytes(name);
    if (!bytes) return null;
    // decodeAudioData detaches its input — hand it a private copy so the
    // cached bytes stay usable for the next context that needs them.
    const buf = await ctx.decodeAudioData(bytes.slice(0));
    const map = decoded.get(ctx) ?? new Map<HitSampleName, AudioBuffer>();
    map.set(name, buf);
    decoded.set(ctx, map);
    return buf;
  } catch {
    return null;                        // bad/missing file → callers fall back
  } finally {
    busy.delete(name);
  }
}

/** The decoded buffer if it's ready right now (never triggers work). */
export function getHitSample(ctx: BaseAudioContext, name: HitSampleName): AudioBuffer | null {
  return decoded.get(ctx)?.get(name) ?? null;
}

/**
 * Play a sampled hit at absolute context time `at` (use `ctx.currentTime` for
 * "now"). Returns false when the sample isn't decoded yet — the caller should
 * fall back to its synthesised voice and try again next hit; the decode is
 * kicked off in the background so the fallback only happens once.
 */
export function playHitSample(
  ctx: AudioContext,
  name: HitSampleName,
  at: number,
  gain: number,
): boolean {
  const buf = getHitSample(ctx, name);
  if (!buf) {
    void ensureHitSample(ctx, name);
    return false;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.value = Math.max(0.0001, gain);
  src.connect(g);
  g.connect(ctx.destination);
  // Never schedule into the past (an already-late hit plays immediately).
  src.start(Math.max(at, ctx.currentTime));
  src.onended = () => { try { src.disconnect(); g.disconnect(); } catch { /* already gone */ } };
  return true;
}

// The bytes cost ~35 KB and need no user gesture — start the download as soon
// as the module is imported so the first hit is never the one that waits.
preloadHitSamples();
